import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Network } from '@capacitor/network';
import { PushNotifications } from '@capacitor/push-notifications';
import { NATIVE_APP_ID } from '../config/appVariant';
import { Share } from '@capacitor/share';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { Browser } from '@capacitor/browser';
import axios from 'axios';
import { watchVoipToken } from './callKit';
import { isAndroidFirebaseConfigured } from './notificationSound';

/**
 * Checks whether the app is running in a native mobile wrapper (iOS or Android).
 */
export const isNativeApp = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Gets the current runtime platform: 'ios' | 'android' | 'web'
 */
export const getPlatform = () => {
  return Capacitor.getPlatform();
};

/**
 * Hides the native splash screen with a smooth fade
 */
export const hideSplashScreen = async () => {
  if (!isNativeApp()) return;
  try {
    await SplashScreen.hide({ fadeOutDuration: 350 });
  } catch (err) {
    console.debug('Native splash screen hide skipped:', err.message);
  }
};

/**
 * Sets the native status bar theme according to light/dark mode
 * @param {boolean} isDark - True for dark mode, false for light mode
 */
export const setStatusBarTheme = async (isDark) => {
  if (!isNativeApp()) return;
  try {
    // In Capacitor: Style.Dark means light text/icons (for dark background)
    // Style.Light means dark text/icons (for light background)
    await StatusBar.setStyle({
      style: isDark ? Style.Dark : Style.Light,
    });

    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({
        color: isDark ? '#09090b' : '#ffffff',
      });
    }
  } catch (err) {
    console.debug('StatusBar configuration skipped:', err.message);
  }
};

/**
 * Triggers tactile haptic feedback on mobile devices
 * @param {'light'|'medium'|'heavy'|'selection'|'success'|'warning'|'error'} [type='light']
 */
export const triggerHaptic = async (type = 'light') => {
  if (isNativeApp()) {
    try {
      switch (type) {
        case 'light':
          await Haptics.impact({ style: ImpactStyle.Light });
          break;
        case 'medium':
          await Haptics.impact({ style: ImpactStyle.Medium });
          break;
        case 'heavy':
          await Haptics.impact({ style: ImpactStyle.Heavy });
          break;
        case 'selection':
          await Haptics.selectionChanged();
          break;
        case 'success':
          await Haptics.notification({ type: NotificationType.Success });
          break;
        case 'warning':
          await Haptics.notification({ type: NotificationType.Warning });
          break;
        case 'error':
          await Haptics.notification({ type: NotificationType.Error });
          break;
        default:
          await Haptics.impact({ style: ImpactStyle.Light });
      }
    } catch (_) {}
  } else if (typeof navigator !== 'undefined' && navigator.vibrate) {
    // Web fallback for Android Chrome & mobile browsers supporting navigator.vibrate
    try {
      if (type === 'selection' || type === 'light') navigator.vibrate(8);
      else if (type === 'medium') navigator.vibrate(15);
      else if (type === 'heavy' || type === 'error') navigator.vibrate([20, 40, 20]);
    } catch (_) {}
  }
};

/**
 * Configures mobile virtual keyboard behavior so inputs/chat bars don't get obscured
 */
export const dismissKeyboard = () => {
  const active = document.activeElement;
  if (active && active !== document.body && typeof active.blur === 'function') {
    active.blur();
  }
  if (!isNativeApp()) return;
  Keyboard.hide().catch(() => {});
};

export const configureKeyboard = async () => {
  if (!isNativeApp()) return;
  try {
    await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
  } catch (err) {
    console.debug('Keyboard resize configuration skipped:', err.message);
  }
};

/**
 * Sets up a listener for virtual keyboard show/hide events (iOS and Android).
 * @param {Function} onKeyboardChange - Receives keyboard height in px (0 when hidden)
 * @returns {Function} cleanup function
 */
export const setupKeyboardListener = (onKeyboardChange) => {
  if (typeof onKeyboardChange !== 'function') return () => {};

  const handles = [];
  let isCleanedUp = false;

  if (isNativeApp()) {
    try {
      const isIos = Capacitor.getPlatform() === 'ios';

      // iOS supports keyboardWillShow; Android dispatches keyboardDidShow
      const showEvents = isIos ? ['keyboardWillShow', 'keyboardDidShow'] : ['keyboardDidShow'];
      const hideEvents = isIos ? ['keyboardWillHide', 'keyboardDidHide'] : ['keyboardDidHide'];

      showEvents.forEach((evt) => {
        Keyboard.addListener(evt, (info) => {
          if (!isCleanedUp) {
            onKeyboardChange(info?.keyboardHeight || 0);
          }
        }).then((h) => handles.push(h)).catch(() => {});
      });

      hideEvents.forEach((evt) => {
        Keyboard.addListener(evt, () => {
          if (!isCleanedUp) {
            onKeyboardChange(0);
          }
        }).then((h) => handles.push(h)).catch(() => {});
      });
    } catch (err) {
      console.debug('[NativeBridge] Keyboard listener setup skipped:', err.message);
    }
  } else if (typeof window !== 'undefined' && window.visualViewport) {
    // Mobile browser visualViewport fallback (Safari iOS, Chrome Android, mobile web)
    const vv = window.visualViewport;
    const handleViewportChange = () => {
      if (isCleanedUp) return;
      const heightDiff = Math.max(0, Math.round(window.innerHeight - vv.height));
      const kHeight = heightDiff > 120 ? heightDiff : 0;
      onKeyboardChange(kHeight);
    };

    vv.addEventListener('resize', handleViewportChange);
    vv.addEventListener('scroll', handleViewportChange);

    return () => {
      isCleanedUp = true;
      vv.removeEventListener('resize', handleViewportChange);
      vv.removeEventListener('scroll', handleViewportChange);
      handles.forEach((h) => h?.remove?.());
    };
  }

  return () => {
    isCleanedUp = true;
    handles.forEach((h) => h?.remove?.());
  };
};

/**
 * Sets up Android hardware back button handler
 * @param {Function} handler - Function returning true if back event was handled, or false to navigate back/exit
 */
export const setupBackButton = (handler) => {
  if (!isNativeApp() || Capacitor.getPlatform() !== 'android') {
    return () => {};
  }

  let listenerPromise = App.addListener('backButton', (data) => {
    if (typeof handler === 'function') {
      const handled = handler(data);
      if (!handled && data.canGoBack) {
        window.history.back();
      }
    }
  });

  return () => {
    listenerPromise.then((handle) => handle.remove()).catch(() => {});
  };
};

let globalDeepLinkHandler = null;
let pendingDeepLinkPath = null;
let lastDispatchedPath = '';
let lastDispatchedTimestamp = 0;
let deepLinkListenersInitialized = false;

const extractTargetPath = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  // Ignore internal WebView schemes
  if (
    trimmed.startsWith('capacitor://') ||
    trimmed.startsWith('http://localhost') ||
    trimmed.startsWith('https://localhost')
  ) {
    return null;
  }

  try {
    let path = '';
    if (trimmed.startsWith('bazzef://')) {
      const withoutScheme = trimmed.replace(/^bazzef:\/\/?\/?/, '');
      path = withoutScheme.startsWith('/') ? withoutScheme : `/${withoutScheme}`;
    } else {
      const parsed = new URL(trimmed);
      path = parsed.pathname || '/';
      if (parsed.search) path += parsed.search;
      if (parsed.hash) path += parsed.hash;
    }

    // Strip hostname if present in custom scheme paths (e.g. bazzef://bazzef.com/post/123)
    path = path.replace(/^\/(?:www\.)?bazzef\.com(?:\/|$)/, '/');

    if (!path || path === '/' || path === '/index.html') {
      return null;
    }

    return path;
  } catch (err) {
    console.warn('[NativeBridge] Failed to parse URL:', rawUrl, err);
    return null;
  }
};

const dispatchDeepLink = (rawUrl) => {
  const targetPath = extractTargetPath(rawUrl);
  if (!targetPath) return;

  const now = Date.now();
  // Prevent duplicate rapid-fire dispatches for the identical path within 500ms
  if (targetPath === lastDispatchedPath && now - lastDispatchedTimestamp < 500) {
    return;
  }
  lastDispatchedPath = targetPath;
  lastDispatchedTimestamp = now;

  console.log('[NativeBridge] Routing deep link to:', targetPath, 'from raw:', rawUrl);

  if (typeof globalDeepLinkHandler === 'function') {
    globalDeepLinkHandler(targetPath);
  } else {
    // Hold in queue until router attaches
    pendingDeepLinkPath = targetPath;
  }
};

/**
 * Initializes persistent native App listeners that survive across React unmounts
 */
const initNativeAppListeners = () => {
  if (!isNativeApp() || deepLinkListenersInitialized) return;
  deepLinkListenersInitialized = true;

  // 1. Cold launch URL
  App.getLaunchUrl()
    .then((launchData) => {
      if (launchData && launchData.url) {
        dispatchDeepLink(launchData.url);
      }
    })
    .catch(() => {});

  // 2. Active / background appUrlOpen listener (persistent singleton)
  App.addListener('appUrlOpen', (data) => {
    if (data && data.url) {
      dispatchDeepLink(data.url);
    }
  });
};

/**
 * Connects React Router's navigate callback to the native deep link pipeline
 * @param {Function} onUrlOpen - Callback receiving the parsed URL path/slug
 */
export const setupDeepLinks = (onUrlOpen) => {
  if (!isNativeApp()) return () => {};

  initNativeAppListeners();

  globalDeepLinkHandler = onUrlOpen;

  // Flush any pending link received before router was ready
  if (pendingDeepLinkPath && typeof onUrlOpen === 'function') {
    const toFlush = pendingDeepLinkPath;
    pendingDeepLinkPath = null;
    setTimeout(() => {
      onUrlOpen(toFlush);
    }, 100);
  }

  return () => {
    // Only detach handler, do NOT remove native Capacitor listener
    if (globalDeepLinkHandler === onUrlOpen) {
      globalDeepLinkHandler = null;
    }
  };
};

/**
 * Initializes network status monitor
 * @param {Function} onStatusChange - Callback receiving boolean isConnected
 */
export const setupNetworkListener = (onStatusChange) => {
  if (!isNativeApp()) return () => {};

  // Check initial state
  Network.getStatus().then((status) => {
    if (typeof onStatusChange === 'function') {
      onStatusChange(status.connected);
    }
  }).catch(() => {});

  let listenerPromise = Network.addListener('networkStatusChange', (status) => {
    if (typeof onStatusChange === 'function') {
      onStatusChange(status.connected);
    }
  });

  return () => {
    listenerPromise.then((handle) => handle.remove()).catch(() => {});
  };
};

let pendingPushPath = null;
let globalPushNavigateHandler = null;

/**
 * Sets up Native Push Notifications: requests permissions, registers device token with backend,
 * configures notification channels, and handles foreground alerts and tap actions.
 *
 * @param {Function} onNavigate - Callback receiving target path to navigate to when user taps notification
 * @param {Function} [onForegroundMessage] - Optional callback receiving the notification object
 * @returns {Function} Cleanup unregister function
 */
export const setupPushNotifications = (onNavigate, onForegroundMessage) => {
  if (!isNativeApp()) return () => {};

  globalPushNavigateHandler = onNavigate;

  // Flush any pending push navigation received before router attached
  if (pendingPushPath && typeof onNavigate === 'function') {
    const toFlush = pendingPushPath;
    pendingPushPath = null;
    setTimeout(() => {
      onNavigate(toFlush);
    }, 150);
  }

  const cleanups = [];

  // iOS: incoming calls ring through PushKit + CallKit, which needs its own
  // token and works even without notification permission
  const stopVoip = watchVoipToken((token) => {
    localStorage.setItem('bazzef_voip_token', token);
    axios
      .post('/api/notifications/register-device', {
        token,
        platform: 'ios_voip',
        appId: NATIVE_APP_ID,
      })
      .catch((err) => console.warn('[Push] Failed to register VoIP token:', err.message));
  });
  cleanups.push({ remove: stopVoip });

  const init = async () => {
    try {
      // 1. On Android 8+, create high-priority notification channel for heads-up alerts
      if (Capacitor.getPlatform() === 'android') {
        try {
          // Standard messages channel (default sound)
          await PushNotifications.createChannel({
            id: 'bazzef_messages',
            name: 'Direct Messages & Mentions',
            description: 'Instant notifications for messages and interactions on Bazzef',
            importance: 5, // IMPORTANCE_HIGH — heads-up + sound
            visibility: 1,
            vibration: true,
            lights: true,
          });
          // Dedicated incoming calls channel with custom ringtone.
          // Channel sounds are immutable once created on a device, so the id is
          // versioned: bump it whenever the sound changes.
          await PushNotifications.deleteChannel({ id: 'bazzef_calls' }).catch(() => {});
          await PushNotifications.createChannel({
            id: 'bazzef_calls_v2',
            name: 'Incoming Voice Calls',
            description: 'Ringing alert for incoming Bazzef voice calls',
            importance: 5,
            visibility: 1,
            vibration: true,
            lights: true,
            sound: 'ringtone', // maps to res/raw/ringtone.wav on Android
          });
        } catch (channelErr) {
          console.debug('[Push] Notification channel creation skipped:', channelErr.message);
        }
      }

      // 2. Check and request permissions
      let permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive !== 'granted') {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        console.info('[Push] Push notification permissions not granted:', permStatus.receive);
        return;
      }

      // 3. Attach listeners BEFORE registering with APNs / FCM
      const regHandle = await PushNotifications.addListener('registration', async (tokenData) => {
        const token = tokenData.value;
        console.log('[Push] Device registration token received:', token ? (token.slice(0, 10) + '...') : '');
        localStorage.setItem('bazzef_push_token', token);
        try {
          await axios.post('/api/notifications/register-device', {
            token,
            platform: Capacitor.getPlatform(),
            appId: NATIVE_APP_ID,
          });
          console.log('[Push] Registered device token successfully with backend');
        } catch (err) {
          console.warn('[Push] Failed to register token with backend:', err.message);
        }
      });
      cleanups.push(regHandle);

      // Listen for registration errors
      const errHandle = await PushNotifications.addListener('registrationError', (err) => {
        console.warn('[Push] Error during registration:', err.error);
      });
      cleanups.push(errHandle);

      // Foreground notification received (silent in foreground, handled by in-app UI)
      const recvHandle = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.debug('[Push] Notification received in foreground (suppressed by presentationOptions):', notification?.id);
        if (typeof onForegroundMessage === 'function') {
          onForegroundMessage(notification);
        }
      });
      cleanups.push(recvHandle);

      // Notification action performed (user tapped the banner)
      const actionHandle = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        console.log('[Push] Notification action performed:', action);
        const data = action.notification?.data || {};

        let targetPath = '/notifications';
        if (data.type === 'message-request') {
          try { sessionStorage.setItem('openMessageRequests', '1'); } catch (_) {}
          targetPath = '/messages';
        } else if (data.type === 'message' && data.userId) {
          targetPath = `/messages/${data.userId}`;
        } else if ((data.type === 'group-message' || data.type === 'group-call') && data.groupId) {
          targetPath = `/messages/group/${data.groupId}`;
        } else if (data.type === 'incoming_call' && (data.callerId || data.userId)) {
          targetPath = `/messages/${data.callerId || data.userId}`;
        } else if (data.type === 'missed_call' && data.userId) {
          targetPath = `/messages/${data.userId}`;
        } else if ((data.type === 'like' || data.type === 'comment' || data.type === 'reply' || data.type === 'quote') && data.postId) {
          targetPath = `/post/${data.postId}`;
        } else if (data.type === 'follow' && data.actorHandle) {
          targetPath = `/profile/${data.actorHandle}`;
        }

        if (typeof globalPushNavigateHandler === 'function') {
          globalPushNavigateHandler(targetPath);
        } else {
          pendingPushPath = targetPath;
        }
      });
      cleanups.push(actionHandle);

      // 4. If token already exists in storage, sync with backend immediately
      const cachedToken = localStorage.getItem('bazzef_push_token');
      if (cachedToken) {
        axios
          .post('/api/notifications/register-device', {
            token: cachedToken,
            platform: Capacitor.getPlatform(),
            appId: NATIVE_APP_ID,
          })
          .catch((e) => console.debug('[Push] Cached token sync:', e.message));
      }
      const cachedVoipToken = localStorage.getItem('bazzef_voip_token');
      if (cachedVoipToken) {
        axios
          .post('/api/notifications/register-device', {
            token: cachedVoipToken,
            platform: 'ios_voip',
            appId: NATIVE_APP_ID,
          })
          .catch((e) => console.debug('[Push] Cached VoIP token sync:', e.message));
      }

      // 5. Register device with APNs / FCM.
      // On Android this calls FirebaseMessaging.getInstance(), which kills the
      // process when this applicationId has no google-services.json.
      if (!(await isAndroidFirebaseConfigured())) {
        console.warn('[Push] Firebase is not configured for this Android app. Push registration skipped.');
        return;
      }
      await PushNotifications.register();
    } catch (err) {
      console.warn('[Push] Failed to initialize push notifications:', err.message);
    }
  };

  init();

  return () => {
    if (globalPushNavigateHandler === onNavigate) {
      globalPushNavigateHandler = null;
    }
    cleanups.forEach((handle) => {
      try {
        handle.remove();
      } catch (_) {}
    });
  };
};

/**
 * Unregisters the current device push token on logout.
 */
export const unregisterPushToken = async () => {
  if (!isNativeApp()) return;
  // The VoIP token too, or this phone would keep ringing for the old account
  await Promise.all(['bazzef_push_token', 'bazzef_voip_token'].map(async (key) => {
    const token = localStorage.getItem(key);
    if (!token) return;
    try {
      await axios.post('/api/notifications/unregister-device', { token });
      localStorage.removeItem(key);
    } catch (err) {
      console.warn('[Push] Failed to unregister device token:', err.message);
    }
  }));
};

/**
 * Shares content natively across iOS, Android, and web with full fallbacks.
 * @param {Object} options
 * @param {string} [options.title] - Share title
 * @param {string} [options.text] - Share text/message
 * @param {string} [options.url] - URL to share
 * @param {string} [options.dialogTitle] - Title for Android chooser
 * @returns {Promise<boolean>} True if shared or handled, false if fallback UI should be shown
 */
export const nativeShare = async ({ title, text, url, dialogTitle } = {}) => {
  // 1. Try Capacitor Native Share Sheet (iOS UIActivityViewController / Android Intent.ACTION_SEND)
  if (isNativeApp()) {
    try {
      await Share.share({
        title: title || 'Bazzef',
        text: text || '',
        url: url || '',
        dialogTitle: dialogTitle || 'Share',
      });
      return true;
    } catch (err) {
      if (
        err?.name === 'AbortError' ||
        err?.message?.toLowerCase().includes('canceled') ||
        err?.message?.toLowerCase().includes('cancelled')
      ) {
        return true;
      }
      console.warn('[NativeBridge] Share.share error, attempting fallback:', err);
    }
  }

  // 2. Web Share API fallback (mobile Safari, Chrome, browsers with navigator.share)
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({
        title: title || 'Bazzef',
        text: text || '',
        url: url || '',
      });
      return true;
    } catch (err) {
      if (err?.name === 'AbortError') return true;
      console.warn('[NativeBridge] navigator.share error:', err);
    }
  }

  // 3. Fallback to custom menu
  return false;
};

/**
 * Locks the app interface orientation to portrait.
 * Enforced on app launch, route changes, and upon exiting media viewers/lightboxes.
 */
export const lockPortrait = async () => {
  if (typeof document !== 'undefined') {
    document.body.classList.remove('allow-landscape');
  }
  if (isNativeApp()) {
    try {
      await ScreenOrientation.lock({ orientation: 'portrait' });
    } catch (err) {
      console.debug('[NativeBridge] ScreenOrientation.lock(portrait) skipped:', err?.message);
    }
  } else if (typeof window !== 'undefined' && window.screen?.orientation?.lock) {
    try {
      await window.screen.orientation.lock('portrait');
    } catch (_) {}
  }
};

/**
 * Unlocks screen orientation so the user can freely view in portrait or rotate to landscape
 * while watching videos, inspecting photos, or viewing full-screen media.
 */
export const allowLandscape = async () => {
  if (typeof document !== 'undefined') {
    document.body.classList.add('allow-landscape');
  }
  if (isNativeApp()) {
    try {
      await ScreenOrientation.unlock();
    } catch (err) {
      console.debug('[NativeBridge] ScreenOrientation.unlock() skipped:', err?.message);
    }
  } else if (typeof window !== 'undefined' && window.screen?.orientation?.unlock) {
    try {
      window.screen.orientation.unlock();
    } catch (_) {}
  }
};

/**
 * Explicitly locks orientation to landscape (e.g. user taps "Rotate to Landscape" button).
 */
export const lockLandscape = async () => {
  if (typeof document !== 'undefined') {
    document.body.classList.add('allow-landscape');
  }
  if (isNativeApp()) {
    try {
      await ScreenOrientation.lock({ orientation: 'landscape' });
    } catch (err) {
      console.debug('[NativeBridge] ScreenOrientation.lock(landscape) skipped:', err?.message);
    }
  } else if (typeof window !== 'undefined' && window.screen?.orientation?.lock) {
    try {
      await window.screen.orientation.lock('landscape');
    } catch (_) {}
  }
};

/**
 * Sets up global fullscreenchange listeners for HTML5 video elements,
 * automatically allowing landscape during fullscreen video playback and
 * re-locking to portrait upon exiting fullscreen.
 */
export const setupOrientationListeners = () => {
  if (typeof document === 'undefined') return () => {};

  const handleFullscreenChange = () => {
    const isFullscreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
    if (isFullscreen) {
      allowLandscape();
    } else {
      lockPortrait();
    }
  };

  document.addEventListener('fullscreenchange', handleFullscreenChange);
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
  document.addEventListener('mozfullscreenchange', handleFullscreenChange);
  document.addEventListener('MSFullscreenChange', handleFullscreenChange);

  // Enforce portrait orientation on initial setup
  lockPortrait();

  return () => {
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
  };
};

let rawWindowOpen = typeof window !== 'undefined' ? window.open : null;

/**
 * Opens an external web URL using the native in-app browser (@capacitor/browser).
 * On iOS, this uses SFSafariViewController (with a "Done" button).
 * On Android, this uses Chrome Custom Tabs (with an "X" button).
 * On Web, it opens in a new tab via window.open.
 *
 * @param {string} url - The URL to open
 */
export const openExternalUrl = async (url) => {
  if (!url || typeof url !== 'string') return;
  const targetUrl = url.trim();
  if (!/^https?:\/\//i.test(targetUrl)) return;

  if (isNativeApp()) {
    try {
      await Browser.open({
        url: targetUrl,
        presentationStyle: 'popover',
      });
      return;
    } catch (err) {
      console.warn('Native Browser.open failed:', err);
    }
  }

  // Web fallback: open in new tab with security attributes
  if (rawWindowOpen) {
    rawWindowOpen.call(window, targetUrl, '_blank', 'noopener,noreferrer');
  } else if (typeof window !== 'undefined' && window.open) {
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Global link interceptor for native mobile apps (iOS & Android).
 * Intercepts clicks on <a> tags with external URLs to prevent them
 * from taking over the Capacitor WebView.
 */
export const setupExternalLinkInterceptor = () => {
  if (typeof document === 'undefined') return () => {};

  const handleLinkClick = (e) => {
    // Check if click was on or inside an anchor element
    const anchor = e.target?.closest?.('a');
    if (!anchor) return;

    const href = anchor.getAttribute('href');
    if (!href) return;

    // Ignore in-page hash links, app-relative paths, and special protocol schemes
    if (
      href.startsWith('#') ||
      href.startsWith('/') ||
      href.startsWith('mailto:') ||
      href.startsWith('tel:') ||
      href.startsWith('sms:') ||
      // eslint-disable-next-line no-script-url
      href.startsWith('javascript:')
    ) {
      return;
    }

    try {
      const url = new URL(anchor.href, window.location.href);

      // Only inspect HTTP/HTTPS external navigations
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return;
      }

      // Check if URL is internal to the application
      const isInternal =
        url.hostname === 'localhost' ||
        url.hostname === '127.0.0.1' ||
        url.hostname === window.location.hostname ||
        url.hostname === 'bazzef.com' ||
        url.hostname.endsWith('.bazzef.com');

      if (!isInternal) {
        // Prevent WebView from navigating away from the Bazzef app
        e.preventDefault();
        e.stopPropagation();
        openExternalUrl(url.href);
      }
    } catch (_) {
      // If URL parsing fails, let default behavior take place
    }
  };

  // Capture phase to intercept before React or native browser handles top-level navigation
  document.addEventListener('click', handleLinkClick, true);

  // Intercept window.open on native apps so inline scripts calling window.open don't hijack WebView
  if (isNativeApp() && typeof window !== 'undefined') {
    if (!rawWindowOpen) {
      rawWindowOpen = window.open;
    }
    window.open = (url, target, features) => {
      if (url && typeof url === 'string' && /^https?:\/\//i.test(url)) {
        try {
          const parsed = new URL(url, window.location.href);
          const isInternal =
            parsed.hostname === window.location.hostname ||
            parsed.hostname === 'bazzef.com' ||
            parsed.hostname.endsWith('.bazzef.com') ||
            parsed.hostname === 'localhost' ||
            parsed.hostname === '127.0.0.1';
          if (!isInternal) {
            openExternalUrl(url);
            return null;
          }
        } catch (_) {}
      }
      return rawWindowOpen ? rawWindowOpen.call(window, url, target, features) : null;
    };
  }

  return () => {
    document.removeEventListener('click', handleLinkClick, true);
    if (rawWindowOpen && typeof window !== 'undefined') {
      window.open = rawWindowOpen;
    }
  };
};




